import { sentryLog } from "../../../tools/sentry";
import { formSet } from "../../gvc/testNewForm/utils";
import { validationFns } from "../../../components/form/validations";
import { IdentityInput } from "../../../schema/types/identity/input";
import { parseLink } from "../../../utils/linkParser/linkParser";

export function getValidator(id: string) {
  return async function validator(v: IdentityInput): Promise<boolean | null> {
    // setup
    formSet(id, `meta.valid`, false);
    formSet(id, `meta.validating`, true);
    formSet(id, `errors`, {});
    formSet(id, `warnings`, {});

    const e: Partial<Record<string, { message: string }>> = {};
    const w: Partial<Record<string, { message: string }>> = {};

    function setError(path: string, message: string) {
      const currentError = e[path];
      if (currentError) {
        return;
      }
      e[path] = { message: message };
    }

    function setWarning(path: string, message: string) {
      const currentError = e[path];
      if (currentError) {
        return;
      }
      const currentWarning = w[path];
      if (currentWarning) {
        return;
      }
      w[path] = { message: message };
    }

    // setup end

    try {
      const fs = validationFns("Name");
      for (const f of [fs.required, fs.name]) {
        const r = f(v.name);
        if (r !== true) {
          setError(`name`, r);
        }
      }

      if (v.description.length > 250) {
        setError(`description`, `Description can be at most 250 characters`);
      }

      if (!v.gvc?.link) {
        setError(`gvc`, "GVC is required");
      } else {
        const parsedLink = parseLink(v.gvc.link, { useInputCtx: true });
        if (!parsedLink.isValid) {
          setError(`gvc`, parsedLink.errorMessage);
        }
      }

      if (v.aws.isEnabled) {
        if (!v.aws.cloudAccount?.link) {
          setError(`aws.cloudAccount`, "Cloud Account is required");
        } else {
          const parsedLink = parseLink(v.aws.cloudAccount.link, { useInputCtx: true });
          if (!parsedLink.isValid) {
            setError(`aws.cloudAccount`, parsedLink.errorMessage);
          }
        }

        if (v.aws.method === "role") {
          if (!v.aws.roleName.value) {
            setError(`aws.roleName.value`, `Role Name is required`);
          }
        }
        if (v.aws.method === "policies") {
          if (v.aws.policyRefs.error) {
            // use manualPolicies when there is an error, because user cannot select from a list and we use manual input
            if (v.aws.policyRefs.manualPolicies.length < 1) {
              setError(`aws.policyRefs.manualPoliciesroot`, `At least 1 policy is required`);
            }
            for (const indexStr in v.aws.policyRefs.manualPolicies) {
              const index = Number(indexStr);
              const item = v.aws.policyRefs.manualPolicies[index];
              if (!item.value) {
                setError(`aws.policyRefs.manualPolicies.${index}.value`, `Value is required`);
                setError(`aws.policyRefs.manualPoliciesroot`, `Policy values are required`);
              }
            }
          } else {
            if (v.aws.policyRefs.selectedPolicies.length < 1) {
              setError(`aws.policyRefs.selectedPoliciesroot`, `At least 1 policy is required`);
            }
            for (const indexStr in v.aws.policyRefs.selectedPolicies) {
              const index = Number(indexStr);
              const item = v.aws.policyRefs.selectedPolicies[index];
              if (!item) {
                setError(`aws.policyRefs.selectedPolicies.${index}`, `Value is required`);
                setError(`aws.policyRefs.selectedPoliciesroot`, `Policy values are required`);
              }
            }
          }
        }
      }

      if (v.azure.isEnabled) {
        if (!v.azure.cloudAccount?.link) {
          setError(`azure.cloudAccount`, "Cloud Account is required");
        } else {
          const parsedLink = parseLink(v.azure.cloudAccount.link, { useInputCtx: true });
          if (!parsedLink.isValid) {
            setError(`azure.cloudAccount`, parsedLink.errorMessage);
          }
        }
      }

      if (v.gcp.isEnabled) {
        if (!v.gcp.cloudAccount?.link) {
          setError(`gcp.cloudAccount`, "Cloud Account is required");
        } else {
          const parsedLink = parseLink(v.gcp.cloudAccount.link, { useInputCtx: true });
          if (!parsedLink.isValid) {
            setError(`gcp.cloudAccount`, parsedLink.errorMessage);
          }
        }

        if (v.gcp.method === "serviceAccount") {
          if (!v.gcp.serviceAccount.value) {
            setError(`gcp.serviceAccount.value`, `Service Account is required`);
          }
        }
      }

      if (v.ngs.isEnabled) {
        if (!v.ngs.cloudAccount?.link) {
          setError(`ngs.cloudAccount`, "Cloud Account is required");
        } else {
          const parsedLink = parseLink(v.ngs.cloudAccount.link, { useInputCtx: true });
          if (!parsedLink.isValid) {
            setError(`ngs.cloudAccount`, parsedLink.errorMessage);
          }
        }

        for (const indexStr in v.ngs.pub.allow) {
          const index = Number(indexStr);
          const item = v.ngs.pub.allow[index];
          if (!item.value) {
            setError(`ngs.pub.allow.${index}.value`, "Value is required");
            setError(`ngs.pub.allowroot`, "Either type a value or remove the empty item");
          }
        }
        for (const indexStr in v.ngs.pub.deny) {
          const index = Number(indexStr);
          const item = v.ngs.pub.deny[index];
          if (!item.value) {
            setError(`ngs.pub.deny.${index}.value`, "Value is required");
            setError(`ngs.pub.denyroot`, "Either type a value or remove the empty item");
          }
        }
        for (const indexStr in v.ngs.sub.allow) {
          const index = Number(indexStr);
          const item = v.ngs.sub.allow[index];
          if (!item.value) {
            setError(`ngs.sub.allow.${index}.value`, "Value is required");
            setError(`ngs.sub.allowroot`, "Either type a value or remove the empty item");
          }
        }
        for (const indexStr in v.ngs.sub.deny) {
          const index = Number(indexStr);
          const item = v.ngs.sub.deny[index];
          if (!item.value) {
            setError(`ngs.sub.deny.${index}.value`, "Value is required");
            setError(`ngs.sub.denyroot`, "Either type a value or remove the empty item");
          }
        }

        let xn: number;
        if (v.ngs.resp.max.length > 0) {
          xn = Number(v.ngs.resp.max);
          if (xn < -1) {
            setError(`ngs.resp.max`, `Response Max must be at least -1`);
          }
        }
        if (v.ngs.subs.length > 0) {
          xn = Number(v.ngs.subs);
          if (xn < -1) {
            setError(`ngs.subs`, `Subs must be at least -1`);
          }
        }
        if (v.ngs.data.length > 0) {
          xn = Number(v.ngs.data);
          if (xn < -1) {
            setError(`ngs.data`, `Data must be at least -1`);
          }
        }
        if (v.ngs.payload.length > 0) {
          xn = Number(v.ngs.payload);
          if (xn < -1) {
            setError(`ngs.payload`, `Payload must be at least -1`);
          }
        }
        if (v.ngs.resp.ttl) {
          const regex = /^[0-9]+(ms|s|m|h)$/;
          if (!regex.test(v.ngs.resp.ttl)) {
            setError(`ngs.resp.ttl`, `TTL must be a valid match for regex: ${regex}`);
          }
        }
      }

      // AWS Item Form validation (for modal) - only validate when modal is open
      if (v.nativeNetworkResources?.awsItemForm?.isAddingItem) {
        const awsForm = v.nativeNetworkResources.awsItemForm;
        if (!awsForm.fqdn) {
          setError(`nativeNetworkResources.awsItemForm.fqdn`, "FQDN is required");
        }
        if (!awsForm.name && !awsForm.generatedName) {
          setError(`nativeNetworkResources.awsItemForm.name`, "Name is required");
        }
        if (!awsForm.awsPrivateLink?.endpointServiceName) {
          setError(`nativeNetworkResources.awsItemForm.awsPrivateLink.endpointServiceName`, "Endpoint Service is required");
        }
        if (!awsForm.ports || awsForm.ports.length < 1) {
          setError(`nativeNetworkResources.awsItemForm.portsroot`, "At least one port is required");
        } else {
          for (const indexStr in awsForm.ports) {
            const index = Number(indexStr);
            const item = awsForm.ports[index];
            if (!item.value) {
              setError(`nativeNetworkResources.awsItemForm.ports.${index}.value`, "Port is required");
            }
          }
        }
      }

      // GCP Item Form validation (for modal) - only validate when modal is open
      if (v.nativeNetworkResources?.gcpItemForm?.isAddingItem) {
        const gcpForm = v.nativeNetworkResources.gcpItemForm;
        if (!gcpForm.fqdn) {
          setError(`nativeNetworkResources.gcpItemForm.fqdn`, "FQDN is required");
        }
        if (!gcpForm.name && !gcpForm.generatedName) {
          setError(`nativeNetworkResources.gcpItemForm.name`, "Name is required");
        }
        if (!gcpForm.gcpServiceConnect?.targetService) {
          setError(`nativeNetworkResources.gcpItemForm.gcpServiceConnect.targetService`, "Target Service is required");
        }
        if (!gcpForm.ports || gcpForm.ports.length < 1) {
          setError(`nativeNetworkResources.gcpItemForm.portsroot`, "At least one port is required");
        } else {
          for (const indexStr in gcpForm.ports) {
            const index = Number(indexStr);
            const item = gcpForm.ports[index];
            if (!item.value) {
              setError(`nativeNetworkResources.gcpItemForm.ports.${index}.value`, "Port is required");
            }
          }
        }
      }

      // FQDN Item Form validation (for modal) - only validate when modal is open
      if (v.networkResources?.fqdnItemForm?.isAddingItem) {
        const fqdnForm = v.networkResources.fqdnItemForm;
        if (!fqdnForm.fqdn) {
          setError(`networkResources.fqdnItemForm.fqdn`, "FQDN is required");
        }
        if (!fqdnForm.name && !fqdnForm.generatedName) {
          setError(`networkResources.fqdnItemForm.name`, "Name is required");
        }
        if (!fqdnForm.agent?.link) {
          setError(`networkResources.fqdnItemForm.agent`, "Agent is required");
        }
        if (!fqdnForm.ports || fqdnForm.ports.length < 1) {
          setError(`networkResources.fqdnItemForm.portsroot`, "At least one port is required");
        } else {
          for (const indexStr in fqdnForm.ports) {
            const index = Number(indexStr);
            const item = fqdnForm.ports[index];
            if (!item.value) {
              setError(`networkResources.fqdnItemForm.ports.${index}.value`, "Port is required");
            }
          }
        }
      }

      // IP Item Form validation (for modal) - only validate when modal is open
      if (v.networkResources?.ipItemForm?.isAddingItem) {
        const ipForm = v.networkResources.ipItemForm;
        if (!ipForm.name) {
          setError(`networkResources.ipItemForm.name`, "Name is required");
        }
        if (!ipForm.agent?.link) {
          setError(`networkResources.ipItemForm.agent`, "Agent is required");
        }
        if (!ipForm.ips || ipForm.ips.length < 1) {
          setError(`networkResources.ipItemForm.ipsroot`, "At least one IP is required");
        } else {
          for (const indexStr in ipForm.ips) {
            const index = Number(indexStr);
            const item = ipForm.ips[index];
            if (!item.value) {
              setError(`networkResources.ipItemForm.ips.${index}.value`, "IP is required");
            }
          }
        }
        if (!ipForm.ports || ipForm.ports.length < 1) {
          setError(`networkResources.ipItemForm.portsroot`, "At least one port is required");
        } else {
          for (const indexStr in ipForm.ports) {
            const index = Number(indexStr);
            const item = ipForm.ports[index];
            if (!item.value) {
              setError(`networkResources.ipItemForm.ports.${index}.value`, "Port is required");
            }
          }
        }
      }

      // tags start
      for (const tagIndexStr in v.tags) {
        const tagIndex = Number(tagIndexStr);
        const tag = v.tags[tagIndex];
        if (!tag.name) {
          setError(`tags.${tagIndex}.name`, "Tag key is required");
          setError(`tagsroot`, "Tag keys are required");
        }
      }

      if (v.tags.length > 50) {
        setError(`tagsroot`, "Tags cannot be more than 50 items");
      }
      // tags end

      // setup
      for (const path of Object.keys(e)) {
        const value = e[path];
        formSet(id, `errors.${path}`, value);
      }
      for (const path of Object.keys(w)) {
        const value = w[path];
        formSet(id, `warnings.${path}`, value);
      }

      const hasErrors = Object.keys(e).length > 0;

      formSet(id, `meta.valid`, !hasErrors);
      formSet(id, `meta.validating`, false);

      return hasErrors;
    } catch (e) {
      sentryLog("identity validator error", { "data.error": e.message });
      formSet(id, `errors`, { root: { message: "An unexpected error occurred. This has been logged and we will fix it." } });
      return true;
    }
  };
}
